/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { ContentEditableEvent } from 'react-contenteditable';
import { IDictionary, IRequestContext } from '../../interfaces';
import { isAuthoringEditEnabled } from '../helper';
import { EditError, PROPERTY_DISABLED, PROPERTY_INIT } from './edit-errors';
import { ImageField } from './image-field';
import { LinkField } from './link-field';
import { TextField } from './text-field';
import { VideoField } from './video-field';

export interface IText {
    tag?: string;
    text: string;
    required?: boolean;
    className?: string;
    additionalProperties?: IDictionary<string>;
}

export interface ILink {
    title: string;
    text: string;
    key: string;
    openInNewTab: boolean;
    className?: string;
    innerClassName?: string;
    href?: string;
    role?: string;
    ariaLabel: string;
    additionalProperties?: IDictionary<string>;
    linkTag?: string;
    onClick?(ev: React.SyntheticEvent): void;
}

export interface ITextEditProps {
    requestContext: IRequestContext;
    placeholderText?: string;
    onEdit(event: ContentEditableEvent): void;
}

export interface IEditProps {
    key: object;
    requestContext: IRequestContext;
    imagePropertyName?: string;
    moduleType?: string;
    layout?: string;
}

export interface IEditableFieldProps<T> {
    editPropKey?: object;
    requestContext?: IRequestContext;
    placeholderText?: string;
    disabled?: boolean;
    fieldProps: T;
    type?: FieldType;
    children?: JSX.Element;
    onChange?(event: React.SyntheticEvent): void;
    onMouseOver?(event: React.SyntheticEvent): void;
    onMouseOut?(event: React.SyntheticEvent): void;
    onFocus?(event: React.SyntheticEvent): void;
    onBlur?(event: React.SyntheticEvent): void;
}

interface IEditableFieldState {
    isDisabled: boolean;
}

export enum FieldType {
    Text,
    RichText,
    Image,
    Video,
    Link
}

/**
 * Editable field component, containing all the logic for inline editable properties in editorial preview
 * Current support: text, image
 */
export class EditableField extends React.Component<IEditableFieldProps<any>, IEditableFieldState> {
    private _isRequired: boolean;
    public constructor(props: IEditableFieldProps<any>) {
        super(props);
        this._onChangeHandler = this._onChangeHandler.bind(this);
        this._isRequired = false;
        this.state = {
            isDisabled: props.disabled || true
        };
    }

    public componentDidMount(): void {
        this._checkDisabledStatus();
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public UNSAFE_componentWillReceiveProps(nextProps: IEditableFieldProps<any>): void {
        // hook to handle props changes - when a field is changed between edit enabled/disabled states
        this._checkDisabledStatus();
    }

    public render(): JSX.Element | null {
        const props = this.props;
        const enabled = !this.state.isDisabled;

        switch (props.type) {
            case FieldType.Text:
            case FieldType.RichText:
                props.fieldProps.required = this._isRequired;
                return <TextField disabled={!!!enabled} {...props} onChange={this._onChangeHandler} />;
            case FieldType.Image:
                return <ImageField disabled={!!!enabled} {...props} onChange={this._onChangeHandler} />;
            case FieldType.Video:
                const videoProps: IEditableFieldProps<any> = {
                    ...this.props,
                    disabled: !!!enabled,
                    onChange: this._onChangeHandler
                };
                return videoProps.fieldProps.children ? <VideoField {...videoProps} /> : null;
            case FieldType.Link:
                return <LinkField type={FieldType.Link} {...props} disabled={!!!enabled} onChange={this._onChangeHandler} />;
            default:
                return null;
        }
    }
    private _onChangeHandler(event: React.SyntheticEvent | { target: { value: string } }): void {
        try {
            if (this.props.onChange) {
                const eventObj: any = event;
                this.props.onChange(eventObj);
            }

            if (this.props.editPropKey) {
                const binding: any = this.props.editPropKey;
                const eventTarget: any = event.target;
                binding.$type = eventTarget.value;
            }
        } catch (e) {
            // if property is disabled, disable editing
            if (e instanceof EditError) {
                if (e.name === PROPERTY_DISABLED) {
                    this._updateStatus(true);
                } else if (e.name === PROPERTY_INIT) {
                    this._updateStatus(false);
                    this._isRequired = !!e.required;
                }
            }
        }
    }

    private _checkDisabledStatus(): void {
        if (isAuthoringEditEnabled(this.props.requestContext)) {
            this._onChangeHandler({ target: { value: '__MSDYN365_WYSIWYG_INIT__' } });
        }
    }

    private _updateStatus(status: boolean): void {
        if (status !== this.state.isDisabled) {
            this.setState({
                isDisabled: status
            });
        }
    }
}
